// Capa visual de los recorridos guiados.
//
// Dibuja tres cosas sobre la pantalla real, sin duplicarla: un velo con un
// recorte sobre el elemento del paso, un marco alrededor de ese recorte, y una
// tarjeta con la explicacion y una flecha que apunta al recorte.
//
// El velo son CUATRO paneles alrededor del elemento, y no una mascara SVG. La
// mascara daba esquinas redondeadas perfectas pero solo puede oscurecer: no
// existe forma de desenfocar lo que hay DEBAJO de un SVG. Los paneles si, con
// `backdrop-filter`, y desenfocar y desaturar el fondo es lo que de verdad
// saca al ojo de la pantalla y lo lleva al instructivo — oscurecer solo no
// alcanzaba. Las esquinas del recorte quedan rectas; el marco redondeado que va
// encima tapa la diferencia.
//
// Los cuatro paneles se calculan con coordenadas enteras para que compartan
// borde exacto. Sin eso aparece una costura clara de un pixel entre panel y
// panel cada vez que el elemento se mueve.
//
// El recorrido NO deja tocar la aplicacion mientras esta abierto. Es una
// explicacion, no un ejercicio: si el usuario pudiera navegar por debajo, el
// paso siguiente apuntaria a un elemento que ya no esta en pantalla.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { openDocs } from '@/help/docs';
import { resolveHelp } from '@/help/targets';
import type { TourStep } from '@/help/tours';

/** Aire entre el elemento resaltado y el borde del recorte. */
const PADDING = 6;
/** Distancia entre el recorte y la tarjeta. */
const GAP = 16;
/** Margen minimo contra los bordes de la ventana. */
const MARGIN = 12;

const CARD_WIDTH = 340;

/**
 * Cuanto se espera a que aparezca el elemento de un paso antes de darlo por
 * inexistente. Una pantalla que esta pidiendo sus datos tarda; una seccion que
 * esta cuenta no puede ver no va a aparecer nunca.
 */
const MISSING_GRACE_MS = 4000;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

type Placement = 'top' | 'bottom' | 'left' | 'right';

function measure(el: Element): Rect {
  const r = el.getBoundingClientRect();
  // Enteros: los cuatro paneles del velo se calculan a partir de estos numeros
  // y tienen que encontrarse exactamente. Con decimales queda una costura clara
  // de un pixel entre panel y panel.
  const top = Math.round(r.top - PADDING);
  const left = Math.round(r.left - PADDING);
  return {
    top,
    left,
    width: Math.round(r.right + PADDING) - left,
    height: Math.round(r.bottom + PADDING) - top,
  };
}

/**
 * Elige de que lado poner la tarjeta. Prioriza abajo y arriba porque una
 * tarjeta ancha a los costados deja al elemento pegado al borde; solo va a un
 * costado cuando arriba y abajo no entra.
 */
function choosePlacement(rect: Rect, cardHeight: number, preferred?: Placement): Placement {
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const fits: Record<Placement, boolean> = {
    bottom: vh - (rect.top + rect.height) >= cardHeight + GAP + MARGIN,
    top: rect.top >= cardHeight + GAP + MARGIN,
    right: vw - (rect.left + rect.width) >= CARD_WIDTH + GAP + MARGIN,
    left: rect.left >= CARD_WIDTH + GAP + MARGIN,
  };
  if (preferred && fits[preferred]) return preferred;
  for (const p of ['bottom', 'top', 'right', 'left'] as const) {
    if (fits[p]) return p;
  }
  return 'bottom';
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export interface TourOverlayProps {
  tourTitle: string;
  steps: readonly TourStep[];
  index: number;
  onIndexChange: (next: number) => void;
  onClose: () => void;
}

export default function TourOverlay({
  tourTitle,
  steps,
  index,
  onIndexChange,
  onClose,
}: TourOverlayProps): JSX.Element | null {
  const step = steps[index];
  const [rect, setRect] = useState<Rect | null>(null);
  const [cardHeight, setCardHeight] = useState(180);
  const cardRef = useRef<HTMLDivElement>(null);

  // Un paso puede apuntar a algo que esta cuenta no ve (una seccion detras de
  // un permiso). En vez de romperse, el recorrido lo saltea.
  //
  // Pero primero espera. Una pantalla que todavia esta pidiendo sus datos no
  // tiene ninguno de los elementos que el recorrido señala, y saltear al
  // instante hacia que TODOS los pasos se saltearan uno tras otro y el
  // recorrido se cerrara solo — apretar "Recorrido guiado" y que no pasara
  // nada. Es la diferencia entre "esto no existe" y "esto no llego todavia".
  useEffect(() => {
    if (!step) return undefined;

    let cancelado = false;
    let timer = 0;
    const limite = Date.now() + MISSING_GRACE_MS;

    const buscar = (): void => {
      if (cancelado) return;
      if (document.querySelector(step.target)) return; // aparecio: lo toma el seguimiento
      if (Date.now() < limite) {
        timer = window.setTimeout(buscar, 150);
        return;
      }
      const next = index + 1;
      if (next < steps.length) onIndexChange(next);
      else onClose();
    };

    buscar();
    return () => {
      cancelado = true;
      window.clearTimeout(timer);
    };
  }, [index, onClose, onIndexChange, step, steps.length]);

  // Posicion del recorte. Se recalcula en cada cuadro mientras el recorrido
  // esta abierto: es la unica forma de seguir un layout que todavia se esta
  // acomodando (fuentes que cargan, tablas que terminan de pintar).
  useLayoutEffect(() => {
    if (!step) return undefined;

    // El marco del paso anterior no vale para este: mientras se espera (hasta
    // MISSING_GRACE_MS) al elemento nuevo, sin esto la tarjeta del paso nuevo
    // se pintaba sobre el recuadro del paso viejo — y un paso cuyo elemento no
    // existe (el paginador sin resultados) se mostraba en vez de saltearse.
    setRect(null);

    let frame = 0;
    let centrado = false;

    const track = (): void => {
      const current = document.querySelector(step.target);
      if (current) {
        // El centrado va acá dentro y no antes del bucle: cuando la pantalla
        // todavia esta cargando, el elemento aparece DESPUES de que arranca el
        // recorrido, y buscarlo una sola vez al principio dejaba el bucle sin
        // arrancar y el paso sin medir.
        if (!centrado) {
          current.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
          centrado = true;
        }
        const next = measure(current);
        // Solo cuando de verdad se movio. Antes se llamaba a setRect en cada
        // cuadro con un objeto nuevo, y eso re-renderizaba la capa 60 veces por
        // segundo — con cuatro paneles desenfocando el fondo, eso se nota.
        setRect((prev) =>
          prev &&
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.height === next.height
            ? prev
            : next,
        );
      }
      frame = window.requestAnimationFrame(track);
    };
    track();
    return () => window.cancelAnimationFrame(frame);
  }, [step]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
  }, [index, step]);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        if (index + 1 < steps.length) onIndexChange(index + 1);
        else onClose();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (index > 0) onIndexChange(index - 1);
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [index, onClose, onIndexChange, steps.length]);

  if (!step) return null;

  // Sin marco todavia: el paso nuevo esta esperando (hasta MISSING_GRACE_MS)
  // a que su elemento aparezca. El recorrido sigue abierto — se mantiene el
  // dialogo y el velo entero, sin recorte ni tarjeta — para que no parezca
  // que se cerro y para que un clic afuera no le llegue a la pantalla. Antes
  // se pintaba la tarjeta nueva sobre el marco del paso anterior.
  if (!rect) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Recorrido: ${tourTitle}`}
        aria-busy="true"
        className="fixed inset-0 z-[100]"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            backgroundColor: 'rgb(9 12 22 / 0.74)',
            backdropFilter: 'blur(3px) saturate(0.3)',
            WebkitBackdropFilter: 'blur(3px) saturate(0.3)',
          }}
        />
      </div>
    );
  }

  const placement = choosePlacement(rect, cardHeight, step.placement);
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let cardTop: number;
  let cardLeft: number;
  if (placement === 'bottom' || placement === 'top') {
    cardTop =
      placement === 'bottom' ? rect.top + rect.height + GAP : rect.top - GAP - cardHeight;
    cardLeft = clamp(
      rect.left + rect.width / 2 - CARD_WIDTH / 2,
      MARGIN,
      Math.max(MARGIN, vw - CARD_WIDTH - MARGIN),
    );
  } else {
    cardLeft = placement === 'right' ? rect.left + rect.width + GAP : rect.left - GAP - CARD_WIDTH;
    cardTop = clamp(
      rect.top + rect.height / 2 - cardHeight / 2,
      MARGIN,
      Math.max(MARGIN, vh - cardHeight - MARGIN),
    );
  }

  // Punta de la flecha: sobre el borde de la tarjeta que mira al elemento, y
  // alineada al centro del elemento (no al de la tarjeta, que puede estar
  // corrida contra el borde de la ventana).
  const arrowSize = 12;
  const arrowStyle: Record<string, string> = {};
  if (placement === 'bottom' || placement === 'top') {
    const x = clamp(
      rect.left + rect.width / 2 - cardLeft,
      18,
      Math.max(18, CARD_WIDTH - 18),
    );
    arrowStyle['left'] = `${x - arrowSize / 2}px`;
    arrowStyle[placement === 'bottom' ? 'top' : 'bottom'] = `${-arrowSize / 2}px`;
  } else {
    const y = clamp(rect.top + rect.height / 2 - cardTop, 18, Math.max(18, cardHeight - 18));
    arrowStyle['top'] = `${y - arrowSize / 2}px`;
    arrowStyle[placement === 'right' ? 'left' : 'right'] = `${-arrowSize / 2}px`;
  }

  /**
   * Que dos lados del cuadrado girado quedan a la vista. Depende del lado por
   * el que sale la flecha: con los mismos dos bordes siempre, la punta se veia
   * cortada en tres de los cuatro casos.
   */
  const arrowBorders: Record<Placement, string> = {
    bottom: 'border-l-2 border-t-2',
    top: 'border-r-2 border-b-2',
    right: 'border-l-2 border-b-2',
    left: 'border-r-2 border-t-2',
  };

  const help = step.help ? resolveHelp(step.help) : null;
  const isLast = index === steps.length - 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Recorrido: ${tourTitle}`}
      className="fixed inset-0 z-[100]"
    >
      {/* El velo: cuatro paneles que dejan el hueco en el medio. Oscurecen,
          desenfocan y desaturan todo lo que no es el paso actual. */}
      {(
        [
          { top: 0, left: 0, width: vw, height: rect.top },
          { top: rect.top + rect.height, left: 0, width: vw, height: vh - (rect.top + rect.height) },
          { top: rect.top, left: 0, width: rect.left, height: rect.height },
          {
            top: rect.top,
            left: rect.left + rect.width,
            width: vw - (rect.left + rect.width),
            height: rect.height,
          },
        ] as const
      ).map((panel, i) => (
        <div
          key={i}
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: panel.top,
            left: panel.left,
            width: Math.max(0, panel.width),
            height: Math.max(0, panel.height),
            backgroundColor: 'rgb(9 12 22 / 0.74)',
            backdropFilter: 'blur(3px) saturate(0.3)',
            WebkitBackdropFilter: 'blur(3px) saturate(0.3)',
          }}
        />
      ))}

      {/* El marco del recorte, con un halo que lo separa del velo. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          border: '2px solid #a78bfa',
          borderRadius: 10,
          boxShadow: '0 0 0 5px rgb(124 58 237 / 0.25), 0 0 28px 4px rgb(124 58 237 / 0.35)',
          pointerEvents: 'none',
        }}
      />

      <div
        ref={cardRef}
        style={{
          top: cardTop,
          left: cardLeft,
          width: CARD_WIDTH,
          // Tres sombras a la vez: una profunda que la despega del fondo, un
          // filo de un pixel que le define el borde contra el velo, y un halo
          // del color de marca que la ata visualmente al elemento resaltado.
          boxShadow:
            '0 28px 70px -16px rgb(0 0 0 / 0.75), 0 0 0 1px rgb(124 58 237 / 0.25), 0 0 48px -10px rgb(124 58 237 / 0.55)',
        }}
        className="
          absolute rounded-2xl p-5 space-y-3
          border-2 border-brand/60 dark:border-brand-light/70
          bg-surface dark:bg-[#1a2338]
        "
      >
        <div
          style={arrowStyle}
          aria-hidden="true"
          className={`absolute h-3 w-3 rotate-45 bg-surface dark:bg-[#1a2338] border-brand/60 dark:border-brand-light/70 ${arrowBorders[placement]}`}
        />

        <header className="space-y-1.5">
          <p className="text-[11px] uppercase tracking-widest text-brand dark:text-brand-light font-semibold">
            {tourTitle} · paso {index + 1} de {steps.length}
          </p>
          <h2 className="text-lg font-semibold leading-snug text-ink dark:text-ink-inverted">
            {step.title}
          </h2>
        </header>

        <p className="text-sm text-ink-muted dark:text-ink-inverted-muted leading-relaxed">
          {step.body}
        </p>

        {help ? (
          <button
            type="button"
            onClick={() => void openDocs(help.page, help.anchor)}
            className="text-xs font-medium text-brand hover:underline"
          >
            Leer más en el manual →
          </button>
        ) : null}

        <footer className="flex items-center justify-between gap-2 pt-2 border-t border-surface-border/70 dark:border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-ink-muted hover:text-ink dark:hover:text-ink-inverted"
          >
            Salir
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onIndexChange(index - 1)}
              disabled={index === 0}
              className="min-h-[2.25rem] rounded-lg px-3 text-sm border border-surface-border dark:border-surface-dark-border disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-muted dark:hover:bg-surface-dark-muted"
            >
              Anterior
            </button>
            <button
              type="button"
              autoFocus
              onClick={() => (isLast ? onClose() : onIndexChange(index + 1))}
              className="min-h-[2.25rem] rounded-lg px-4 text-sm bg-brand hover:bg-brand-hover text-white font-medium focus-visible:ring-2 focus-visible:ring-brand-light"
            >
              {isLast ? 'Terminar' : 'Siguiente'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
